import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { Notice } from '@/components/admin/Notice';
import {
  createPreset,
  fetchNotificationTemplates,
  type RuleDef,
} from '@/features/notifications/api';
import { PresetRulesEditor, newPresetRule } from '@/features/notifications/PresetRulesEditor';
import { ApiError } from '@/lib/api/http';

/** 通知プリセットの新規作成（独立ページ / D-292）。 */
export function NotificationPresetCreatePage(): ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const templatesQuery = useQuery({
    queryKey: ['notification-templates'],
    queryFn: fetchNotificationTemplates,
  });

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [rules, setRules] = useState<RuleDef[]>(() => [newPresetRule([])]);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState('{"rules": []}');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canSubmit = code.trim() !== '' && name.trim() !== '';

  async function onCreate(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const definition = jsonMode
        ? (JSON.parse(jsonText) as { rules: RuleDef[] })
        : { rules };
      await createPreset({
        code: code.trim(),
        name: name.trim(),
        definition,
      });
      await queryClient.invalidateQueries({ queryKey: ['notification-presets'] });
      navigate('/masters/notification-presets', {
        state: { notice: 'プリセットを作成しました。' },
      });
    } catch (err) {
      if (err instanceof SyntaxError) setError('JSONの形式が不正です。');
      else setError(err instanceof ApiError ? err.message : '作成に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="通知プリセットの新規作成"
        description="イベント種別に紐づける配信ルールのひな型を作ります。作成後、イベント種別マスタでこのプリセットを選ぶと、その種別のイベント作成時にルールが展開されます。"
        actions={
          <Link to="/masters/notification-presets" className="btn-secondary">
            一覧へ戻る
          </Link>
        }
      />

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      <div className="mb-4 rounded-sm border border-wpborder bg-white px-3 py-3">
        <div className="mb-3 grid max-w-xl grid-cols-1 gap-2">
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">コード（英数字・変更不可）</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="例: monthly-meeting"
              className="w-full rounded-sm border border-wpborder px-3 py-2 font-mono"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">名称</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例: 月例会"
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              if (!jsonMode) {
                setJsonText(JSON.stringify({ rules }, null, 2));
              }
              setJsonMode((m) => !m);
            }}
            className="btn-secondary"
          >
            {jsonMode ? 'フォームで編集' : 'JSONで編集（補助）'}
          </button>
          <button
            type="button"
            disabled={busy || !canSubmit}
            onClick={() => void onCreate()}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            作成する
          </button>
        </div>
      </div>

      <PresetRulesEditor
        rules={rules}
        onChange={setRules}
        templates={templatesQuery.data ?? []}
        jsonMode={jsonMode}
        jsonText={jsonText}
        onJsonTextChange={setJsonText}
      />
    </>
  );
}
